import React, { useMemo, useRef } from 'react'
import type { CSSProperties } from 'react'
import type { AreaChartPoint, AreaChartProps } from './AreaChart.types'
import {
  AREA_CHART_DEFAULT_DATA,
  AREA_CHART_DEFAULT_HEIGHT,
  AREA_CHART_DEFAULT_STROKE_WIDTH,
  AREA_CHART_DEFAULT_WIDTH,
  AREA_CHART_FILL_COLOR,
  AREA_CHART_FILL_OPACITY,
  AREA_CHART_STROKE_COLOR,
} from './AreaChart.types'
import { ChartTooltip, useSeriesChartTooltip } from '../ChartTooltip'
import './AreaChart.css'

type PlotPoint = { x: number; y: number }

const resolveData = (data: AreaChartPoint[]): AreaChartPoint[] => {
  const usable = data.filter((point) => Number.isFinite(point.value))
  return usable.length > 0 ? usable : AREA_CHART_DEFAULT_DATA
}

const buildPlotPoints = (
  data: AreaChartPoint[],
  width: number,
  height: number,
  minValue: number,
  maxValue: number,
  insetY: number,
): PlotPoint[] => {
  const range = Math.max(maxValue - minValue, Number.EPSILON)
  const lastIndex = Math.max(data.length - 1, 1)
  const plotHeight = Math.max(height - insetY * 2, 0)

  return data.map((point, index) => {
    // Span full plot width so first/last points meet the grid edges.
    const x = data.length === 1 ? 0 : (index / lastIndex) * width
    const normalized = (point.value - minValue) / range
    const y = insetY + (1 - normalized) * plotHeight
    return { x, y }
  })
}

const buildLinePath = (points: PlotPoint[]): string => {
  if (points.length === 0) return ''
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ')
}

const buildAreaPath = (
  points: PlotPoint[],
  width: number,
  height: number,
  insetY: number,
): string => {
  if (points.length === 0) return ''
  const line = buildLinePath(points)
  const bottom = height - insetY
  return `${line} L ${width} ${bottom} L 0 ${bottom} Z`
}

const AreaChart: React.FC<AreaChartProps> = ({
  data = AREA_CHART_DEFAULT_DATA,
  width = AREA_CHART_DEFAULT_WIDTH,
  height = AREA_CHART_DEFAULT_HEIGHT,
  strokeColor = AREA_CHART_STROKE_COLOR,
  fillColor = AREA_CHART_FILL_COLOR,
  fillOpacity = AREA_CHART_FILL_OPACITY,
  strokeWidth = AREA_CHART_DEFAULT_STROKE_WIDTH,
  maxValue,
  minValue = 0,
  showTooltip = true,
  seriesName = 'Value',
  formatValue,
  darkMode = false,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const resolvedData = useMemo(() => resolveData(data), [data])
  const insetY = Math.max(strokeWidth / 2 + 1, 2)
  const format = formatValue ?? ((value: number) => String(value))

  const resolvedMax = useMemo(() => {
    if (maxValue != null && Number.isFinite(maxValue)) return maxValue
    const peak = Math.max(...resolvedData.map((point) => point.value))
    return peak > minValue ? peak : minValue + 1
  }, [maxValue, minValue, resolvedData])

  const plotPoints = useMemo(
    () =>
      buildPlotPoints(
        resolvedData,
        width,
        height,
        minValue,
        resolvedMax,
        insetY,
      ),
    [height, insetY, minValue, resolvedData, resolvedMax, width],
  )

  const linePath = useMemo(() => buildLinePath(plotPoints), [plotPoints])
  const areaPath = useMemo(
    () => buildAreaPath(plotPoints, width, height, insetY),
    [height, insetY, plotPoints, width],
  )

  const { tooltip, onMouseMove, onMouseLeave } = useSeriesChartTooltip({
    enabled: showTooltip,
    containerRef,
    plotPoints,
    viewWidth: width,
    viewHeight: height,
  })

  const activePoint =
    tooltip.visible && tooltip.index >= 0 ? resolvedData[tooltip.index] : null

  const classNames = [
    'ds-area-chart',
    showTooltip ? 'ds-area-chart--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const rootStyle = {
    ...style,
    '--ds-area-chart-height': `${height}px`,
  } as CSSProperties

  return (
    <div
      ref={containerRef}
      className={classNames}
      style={rootStyle}
      role="img"
      aria-label={ariaLabel ?? 'Area chart'}
      onMouseMove={showTooltip ? onMouseMove : undefined}
      onMouseLeave={showTooltip ? onMouseLeave : undefined}
    >
      <svg
        className="ds-area-chart__svg"
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        aria-hidden="true"
        overflow="visible"
      >
        <path
          d={areaPath}
          fill={fillColor}
          fillOpacity={fillOpacity}
          stroke="none"
        />
        <path
          d={linePath}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="miter"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {showTooltip && tooltip.visible ? (
        <div
          className={`ds-chart-tooltip-layer${
            darkMode ? ' ds-chart-tooltip-layer--dark' : ''
          }`}
          aria-hidden="true"
        >
          <span
            className="ds-chart-tooltip-crosshair"
            style={{ left: tooltip.pointX }}
          />
          <span
            className="ds-chart-tooltip-marker"
            style={{
              left: tooltip.pointX,
              top: tooltip.pointY,
              background: strokeColor,
            }}
          />
        </div>
      ) : null}

      {showTooltip && activePoint ? (
        <ChartTooltip
          visible
          darkMode={darkMode}
          title={activePoint.label ?? `Point ${tooltip.index + 1}`}
          x={tooltip.pointX}
          y={tooltip.pointY}
          items={[
            {
              label: seriesName,
              value: format(activePoint.value),
              color: strokeColor,
            },
          ]}
        />
      ) : null}
    </div>
  )
}

export default AreaChart
